import React, { useEffect, useState } from "react";
import { Plus, Ticket, Sparkles, Star, Footprints, UserMinus, CalendarClock, ChevronRight, History, Trophy, HeartHandshake } from "lucide-react";
import { CarteEnfant, Profs } from "./Cartes";
import { jourLong } from "../lib/agenda";
import { FicheEnfant } from "./Profil";
import { FormulaireAbsence, AbsencesAnnoncees } from "./Absence";
import { Lien, versPageDuSite } from "./Lien";
import { Groupe, Ligne, TitreGroupe } from "./ui";
import { useChantiers } from "../lib/useChantiers";
import { RappelBillets } from "./MesBillets";
import { FeuillePhoto, RondPhoto } from "./PhotoEleve";

/* Le raccourci « Réserver un cours privé » (Jen, 31.10.2026), là où la réservation en ligne
   est ouverte (version de test d'abord). Marqué app=1 : la page dit « ← Retour à l'app »
   (02.11.2026). */
const LignePrive = ({ seule = false }) => {
  const ouvert = useChantiers().cours_prives;
  if (!ouvert) return null;
  const ligne = (
    <Ligne Icone={CalendarClock} couleur="#7C3AED" titre="Réserver un cours privé"
      sousTitre="Chant, danse ou théâtre, seul·e avec un prof" href={versPageDuSite("/inscription/cours-prive")} testId="espace-cours-prive" />
  );
  return seule ? <Groupe>{ligne}</Groupe> : ligne;
};

/* Chaque sorte de réservation a sa teinte : la tuile de la date en prend la couleur, le
   reste de la carte est celui d'un cours. Les auditions et les créneaux de bénévole des
   spectacles de juin rejoignent les autres (Jen, 02.11.2026 : « toutes les réservations que
   fait une famille doivent être visibles dans leur app - sous mes cours »). `action` : le
   libellé du lien personnel (changer ou annuler), quand le serveur donne ce lien. */
const TYPES = {
  essai: { label: "Cours d'essai", Icone: Footprints, couleur: "#0E7490", fond: "#E0F7F8" },
  stage: { label: "Stage", Icone: Sparkles, couleur: "#B45309", fond: "#FEF3C7" },
  workshop: { label: "Workshop", Icone: Star, couleur: "#DB2777", fond: "#FCE7F3" },
  spectacle: { label: "Spectacle", Icone: Ticket, couleur: "#9333EA", fond: "#F3E8FF", action: "Modifier ou annuler ma réservation" },
  prive: { label: "Cours privé", Icone: CalendarClock, couleur: "#7C3AED", fond: "#EDE9FE" },
  audition: { label: "Audition", Icone: Trophy, couleur: "#1D4ED8", fond: "#DBEAFE" },
  benevole: { label: "Bénévole", Icone: HeartHandshake, couleur: "#BE123C", fond: "#FFE4E6",
    action: "Changer de créneau ou annuler" },
};

const JOURS_COURTS = ["DIM", "LUN", "MAR", "MER", "JEU", "VEN", "SAM"];
const MOIS_COURTS = ["JANV", "FÉVR", "MARS", "AVR", "MAI", "JUIN", "JUIL", "AOÛT", "SEPT", "OCT", "NOV", "DÉC"];
const JOURS_SEMAINE = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const leJour = (iso) => new Date(`${iso}T12:00:00`);
/* « vendredi 23 octobre » (le jour de la semaine compte pour un rendez-vous). */
const jourEnLettres = (iso) => `${JOURS_SEMAINE[leJour(iso).getDay()]} ${jourLong(iso).replace(/ \d{4}$/, "")}`;

/* Une réservation est-elle celle de cet élève ? (son id, ou son prénom et son nom) */
const normal = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
const estA = (r, e) => (r.eleve_id && r.eleve_id === e.id)
  || (!!r.personne && (normal(r.personne) === normal(`${e.prenom} ${e.nom || ""}`) || normal(r.personne).split(" ")[0] === normal(e.prenom)));

/* Le jour de la réservation (AAAA-MM-JJ), donné par le serveur — à défaut, une date déjà
   chiffrée. Sans jour lisible, la réservation reste « à venir », en fin de liste. */
const jourDe = (r) => r.jour || (ISO.test(String(r.date || "")) ? r.date : "");
/* Passée dès la fin du cours (ou du dernier jour du stage) : elle descend alors dans
   « Réservations passées » (Jen, 01.11.2026 : « jusqu'à ce qu'il ait lieu, et après peut
   descendre dans réservations passées »). */
const estPassee = (r, maintenant) => {
  const jour = r.jusqu_au || jourDe(r);
  if (!jour) return false;
  const heure = !r.jusqu_au && /^\d{2}:\d{2}$/.test(r.fin || "") ? r.fin : "23:59";
  return new Date(`${jour}T${heure}:00`) < maintenant;
};
const cle = (r) => `${jourDe(r) || "9999-99-99"} ${r.debut || ""}`;

/* La tuile de gauche, comme celle d'un cours (« JEU 16:00 »), mais datée : « JEU 5 NOV ». */
const TuileDate = ({ r, passee }) => {
  const t = TYPES[r.type] || TYPES.essai;
  const jour = jourDe(r);
  const fond = passee ? "#F1F5F9" : t.fond;
  const encre = passee ? "#94A3B8" : t.couleur;
  if (!jour) {
    return (
      <div className="w-[54px] h-[54px] shrink-0 self-start rounded-2xl grid place-items-center" style={{ background: fond, color: encre }}>
        <t.Icone size={22} strokeWidth={2.2} />
      </div>
    );
  }
  const d = leJour(jour);
  return (
    <div className="w-[54px] shrink-0 self-start rounded-2xl text-center py-1.5" style={{ background: fond }}
      data-testid="espace-resa-date">
      <div className="text-[10.5px] font-bold tracking-[0.08em]" style={{ color: encre }}>{JOURS_COURTS[d.getDay()]}</div>
      <div className="text-[20px] font-semibold tabular-nums leading-none mt-0.5" style={passee ? { color: "#64748B" } : undefined}>{d.getDate()}</div>
      <div className="text-[10.5px] font-bold tracking-[0.08em] mt-1 text-[var(--muted)]">{MOIS_COURTS[d.getMonth()]}</div>
    </div>
  );
};

/* « Salle A » pour un code de salle, le lieu tel quel sinon. */
const lieuLisible = (l) => (!l ? "" : /^[a-z0-9]{1,3}$/i.test(l) ? `Salle ${l}` : l);

/* Sous le titre : l'horaire et la salle, comme un cours ; la sorte de réservation quand le
   titre ne la dit pas (« Chant ados » → « Cours d'essai ») ; le nombre de places. Un créneau
   de bénévole dit d'abord le spectacle et son heure (« Héros & Vilains, spectacle à 18h ») :
   l'heure d'arrivée passe par le groupe WhatsApp des bénévoles. */
const sousTitre = (r, passee, qui) => {
  const t = TYPES[r.type] || TYPES.essai;
  const jour = jourDe(r);
  const dit = normal(r.titre).includes(normal(t.label).replace(/s$/, ""));
  return [
    dit ? "" : t.label,
    r.type === "benevole" ? r.detail : "",
    jour ? "" : r.date,
    r.jusqu_au ? `jusqu'au ${jourEnLettres(r.jusqu_au)}` : "",
    r.debut && r.fin ? `${r.debut}–${r.fin}` : r.debut,
    lieuLisible(r.lieu),
    r.type === "spectacle" || (r.type === "essai" && !jour) ? r.detail : "",
    passee && r.prof ? `avec ${r.prof}` : "",
    qui,
  ].filter(Boolean).join(" · ");
};

/* « Changer l'horaire ou annuler » : la page du site s'ouvre DANS l'app, avec « ← Retour à
   l'app » en haut (app=1). */
const ActionResa = ({ r }) => {
  const href = versPageDuSite(r.lien);
  const props = {
    className: "inline-flex items-center gap-0.5 text-[13px] font-semibold mt-1.5 min-h-[32px]",
    style: { color: "var(--primary)" }, "data-testid": "espace-resa-action",
  };
  const texte = <>{TYPES[r.type]?.action || "Changer l'horaire ou annuler"} <ChevronRight size={15} /></>;
  return String(href).startsWith("/") ? <Lien href={href} {...props}>{texte}</Lien> : <a href={href} {...props}>{texte}</a>;
};

/* Une réservation présentée comme un cours (Jen, 01.11.2026 : « les réservations de cours
   privé ou workshop devraient être comme les cours plus haut ») : la tuile datée à gauche,
   le titre, l'horaire et la salle, le prof avec sa photo, puis l'action, discrète. */
const CarteResa = ({ r, passee = false, qui = "" }) => (
  <div className="flex gap-3.5 px-4 py-3.5" data-testid={`espace-reservation-${r.type}`} data-passee={passee ? "oui" : undefined}>
    <TuileDate r={r} passee={passee} />
    <div className="min-w-0 flex-1">
      <div className="text-[15px] font-semibold leading-snug" style={passee ? { color: "#475569" } : undefined}>{r.titre}</div>
      <div className="text-[12.5px] text-[var(--muted)] mt-0.5">{sousTitre(r, passee, qui)}</div>
      {/attente/i.test(r.statut || "") && (
        <span className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 mt-1.5"
          style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="espace-resa-attente">Liste d'attente</span>
      )}
      {/* Cours privé manqué sans annuler (01.11.2026) : l'école l'a noté, la facture est partie. */}
      {/^absent/i.test(r.statut || "") && (
        <span className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 mt-1.5"
          style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="espace-resa-absent">Absent·e · facture envoyée par e-mail</span>
      )}
      {!passee && r.prof && <Profs texte={r.prof} />}
      {!passee && r.lien && <ActionResa r={r} />}
    </div>
  </div>
);

/* Les réservations à venir d'une personne (ou de toute la famille), dans l'ordre du
   calendrier, sous son nom (« Réservations de Léa »). */
const Reservations = ({ liste, titre, testid = "espace-reservations" }) => {
  if (!liste?.length) return null;
  return (
    <section className="pt-1" data-testid={testid}>
      <TitreGroupe>{titre}</TitreGroupe>
      <Groupe>
        {liste.map((r, n) => <CarteResa key={`${r.type}-${cle(r)}-${n}`} r={r} />)}
      </Groupe>
    </section>
  );
};

/* Ce qui n'est à aucun élève de la liste : la réservation d'un parent (workshop heels) sous son
   nom, celles de toute la famille (places de spectacle) ensemble. */
const AutresReservations = ({ liste, enfants }) => {
  const reste = (liste || []).filter((r) => !enfants.some((e) => estA(r, e)));
  const groupes = {};
  reste.forEach((r) => { (groupes[r.personne || ""] ||= []).push(r); });
  return Object.entries(groupes).map(([qui, l]) => (
    <Reservations key={qui || "famille"} liste={l} testid={`espace-reservations-${qui ? "personne" : "famille"}`}
      titre={qui ? `Réservations de ${qui}` : (enfants.length ? "Pour toute la famille" : "Mes réservations")} />
  ));
};

/* « Réservations passées », repliée : on la déplie pour retrouver un cours privé, un stage
   ou un workshop déjà faits — les plus récents d'abord. */
const Passees = ({ liste, enfant }) => {
  const [ouvert, setOuvert] = useState(false);
  if (!liste.length) return null;
  return (
    <Groupe testId="espace-reservations-passees">
      <Ligne Icone={History} couleur="#94A3B8" titre="Réservations passées"
        sousTitre={liste.length > 1 ? `${liste.length} réservations` : "1 réservation"}
        onClick={() => setOuvert(!ouvert)} ouvert={ouvert} testId="espace-passees-ouvrir" />
      {ouvert && liste.map((r, n) => (
        <CarteResa key={`${r.type}-${cle(r)}-${n}`} r={r} passee
          qui={r.personne && !(enfant && estA(r, enfant)) ? r.personne.split(" ")[0] : ""} />
      ))}
    </Groupe>
  );
};

/* Une couleur par élève : on reconnaît d'un coup d'œil qui on regarde. */
const TEINTES = ["#00C6CE", "#EC4899", "#F59E0B", "#9333EA", "#22C55E"];

/* Le choix de l'élève : une tuile par enfant (initiale, prénom, nombre de cours), côte à
   côte. Plus lisible que les segments gris d'avant : on voit tous ses enfants à la fois.
   Le rond porte la photo ou l'avatar choisi par la famille ; le toucher ouvre le choix
   (`onPhoto`, chantier « photos_eleves »). Pas pour une personne qui n'a qu'une réservation
   (`seulementResa`) : elle n'a pas de fiche élève, elle garde sa lettre. */
const Eleves = ({ enfants, choisi, onChoisir, onPhoto }) => (
  <div className={`grid gap-2.5 ${enfants.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}
    data-testid="espace-selecteur-enfant">
    {enfants.map((e, n) => {
      const actif = e.id === choisi;
      const teinte = TEINTES[n % TEINTES.length];
      const photo = onPhoto && !e.seulementResa;
      return (
        <div key={e.id} className="relative rounded-[18px] bg-white transition-all min-w-0"
          style={{
            border: actif ? `2px solid ${teinte}` : "1px solid var(--border)",
            boxShadow: actif ? `0 6px 16px ${teinte}33` : "none",
            opacity: actif ? 1 : 0.8,
          }}>
          <button type="button" onClick={() => onChoisir(e.id)} aria-pressed={actif}
            className="w-full rounded-[16px] px-3 pb-3 pt-[62px] flex flex-col items-center gap-1.5 text-center min-w-0"
            data-testid={`espace-enfant-pastille-${e.id}`}>
            <span className="text-[15px] font-semibold leading-tight truncate max-w-full">{e.prenom}</span>
            <span className="text-[12px] text-[var(--muted)] leading-tight">
              {/* Deux cartes 10 au même cours : un seul cours par semaine. */}
              {e.seulementResa ? e.resaTexte
                : e.cours.length ? `${new Set(e.cours.map((c) => c.cours_id || c.id)).size} cours par semaine` : (e.resaTexte || "Pas de cours")}
            </span>
          </button>
          {/* Le rond, posé sur la tuile : touché, il ouvre la photo (et choisit l'élève). */}
          <span className={`absolute top-3 left-1/2 -translate-x-1/2 ${photo ? "" : "pointer-events-none"}`}>
            <RondPhoto eleve={e} teinte={teinte} taille={44} testId={`espace-enfant-rond-${e.id}`}
              onClick={photo ? () => { onChoisir(e.id); onPhoto(e.id, teinte); } : undefined} />
          </span>
        </div>
      );
    })}
  </div>
);

/* Mes cours. Avec plusieurs enfants, une tuile par enfant en haut : on choisit qui on
   regarde, plutôt que de faire défiler toutes les fiches. */
/* L'enfant regardé dans « Mes cours » est retenu : « Tom ne pourra pas venir ? » ouvre
   directement Tom, pas le premier enfant (une absence annoncée pour Tom partait sur Léa,
   dont le cours du jeudi porte presque le même nom). */
const CLE_ENFANT = "espace_enfant";
export const choisirEnfant = (id) => {
  try { sessionStorage.setItem(CLE_ENFANT, id); } catch { /* navigation privée */ }
};

/* La tuile d'un enfant seul, en haut de sa carte : un fond dégradé, comme avant. */
const DEGRADE = "linear-gradient(140deg,#00C6CE,#0F1B2D)";

export const TabCours = ({ moi, onAttestation, onRecharger, onOnglet }) => {
  // La photo ou l'avatar d'un élève (Jen, 02.11.2026), tant que le chantier est ouvert.
  // Ce qui vient d'être choisi s'affiche tout de suite, sans attendre que /moi revienne.
  const photosOuvertes = !!useChantiers().photos_eleves;
  const [feuille, setFeuille] = useState(null);
  const [retouches, setRetouches] = useState({});
  const ouvrirPhoto = photosOuvertes ? (id, teinte) => setFeuille({ id, teinte }) : undefined;
  useEffect(() => { setRetouches({}); }, [moi]);     // /moi relu : il fait foi
  // À venir dans l'ordre du calendrier, sous les cours de la personne ; passées à part, les
  // plus récentes d'abord — celles des autres enfants restent sur leur tuile.
  const maintenant = new Date();
  const toutes = moi.reservations || [];
  const aVenir = toutes.filter((r) => !estPassee(r, maintenant)).sort((a, b) => cle(a).localeCompare(cle(b)));
  // Une personne qui a une réservation à son nom sans être élève de la famille (la maman au
  // workshop heels) a SA tuile, à côté des enfants (Jen, 02.11.2026 : « pourquoi les workshops
  // heels ne vont pas sous le nom de la personne ? ») — avant, sa réservation tombait en bas,
  // sous l'enfant choisi, comme si c'était la sienne.
  const autres = [];
  toutes.forEach((r) => {
    if (!r.personne || moi.enfants.some((e) => estA(r, e))) return;
    const [prenom, ...reste] = r.personne.trim().split(/\s+/);
    const p = { id: `personne:${normal(r.personne)}`, prenom, nom: reste.join(" "), cours: [], seulementResa: true };
    if (!autres.some((x) => x.id === p.id)) autres.push(p);
  });
  autres.forEach((p) => {
    const n = aVenir.filter((r) => estA(r, p)).length;
    p.resaTexte = n ? `${n} réservation${n > 1 ? "s" : ""}` : "Réservations passées";
  });
  // Une fiche sans cours mais avec des réservations à venir (la maman inscrite au workshop
  // heels) : « 2 réservations » sur sa tuile, plutôt que « Pas de cours ».
  const enfants = moi.enfants.map((e) => (retouches[e.id] ? { ...e, ...retouches[e.id] } : e)).map((e) => {
    if ((e.cours || []).length) return e;
    const n = aVenir.filter((r) => estA(r, e)).length;
    return n ? { ...e, resaTexte: `${n} réservation${n > 1 ? "s" : ""}` } : e;
  });
  const personnes = enfants.length ? [...enfants, ...autres] : [];
  const [choisi, setChoisiBrut] = useState(() => {
    let retenu = "";
    try { retenu = sessionStorage.getItem(CLE_ENFANT) || ""; } catch { /* rien */ }
    return personnes.some((e) => e.id === retenu) ? retenu : (personnes[0]?.id || "");
  });
  const setChoisi = (id) => { choisirEnfant(id); setChoisiBrut(id); };
  const plusieurs = personnes.length > 1;
  const visibles = plusieurs ? personnes.filter((e) => e.id === choisi) : personnes;
  const passees = toutes.filter((r) => estPassee(r, maintenant)).sort((a, b) => cle(b).localeCompare(cle(a)))
    .filter((r) => visibles.some((e) => estA(r, e)) || !personnes.some((e) => estA(r, e)))
    .slice(0, 30);

  if (!moi.enfants.length) {
    return (
      <div className="space-y-6">
        <AutresReservations liste={aVenir} enfants={[]} />
        <RappelBillets billets={moi.billets} onOnglet={onOnglet} />
        <Passees liste={passees} />
        <div className="rounded-[22px] bg-white px-5 py-6 text-center"
          style={{ border: "1px solid var(--border)" }} data-testid="espace-cours-vide">
          <p className="text-[15px] font-semibold">Aucun cours pour l'instant</p>
          <p className="text-sm text-[var(--muted)] mt-1">
            Choisis un cours dans le planning : il apparaîtra ici dès l'inscription.
          </p>
          <Lien href="/inscription" className="btn-primary mt-5 inline-flex" data-testid="espace-sinscrire">
            M'inscrire à un cours
          </Lien>
        </div>
        <LignePrive seule />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="espace-tab-cours">
      {plusieurs && <Eleves enfants={personnes} choisi={choisi} onChoisir={setChoisi} onPhoto={ouvrirPhoto} />}

      {visibles.filter((e) => e.seulementResa).map((p) => (
        aVenir.some((r) => estA(r, p))
          ? <Reservations key={p.id} liste={aVenir.filter((r) => estA(r, p))}
              titre={`Réservations de ${p.prenom}`} testid="espace-reservations-personne" />
          : <p key={p.id} className="text-sm text-[var(--muted)] px-1" data-testid="espace-personne-rien">Rien à venir pour {p.prenom}.</p>
      ))}

      {visibles.filter((e) => !e.seulementResa).map((e) => (
        <React.Fragment key={e.id}>
          <CarteEnfant enfant={e} onAttestation={onAttestation} sansEntete={plusieurs}
            rond={<RondPhoto eleve={e} teinte={DEGRADE} taille={48} testId={`espace-enfant-rond-${e.id}`}
              onClick={ouvrirPhoto ? () => ouvrirPhoto(e.id, DEGRADE) : undefined} />}
            reservations={<Reservations liste={aVenir.filter((r) => estA(r, e))}
              titre={`Réservations de ${e.prenom}`} testid={`espace-reservations-${e.id}`} />}
            annoncees={<AbsencesAnnoncees enfant={e} onChange={onRecharger} />}
            absence={(fini) => <FormulaireAbsence enfant={e} onChange={onRecharger} onFini={fini} />}>
            <FicheEnfant enfant={e} onEnregistre={onRecharger} />
          </CarteEnfant>
        </React.Fragment>
      ))}

      <AutresReservations liste={aVenir} enfants={personnes} />
      <RappelBillets billets={moi.billets} onOnglet={onOnglet} />
      <Passees liste={passees} enfant={visibles.length === 1 ? visibles[0] : null} />

      <Groupe>
        <Ligne Icone={Plus} couleur="var(--primary)" titre="Ajouter un cours"
          sousTitre="Pour un enfant déjà inscrit ou une autre personne"
          href="/inscription" testId="espace-ajouter-cours" />
        <LignePrive />
        <Ligne Icone={UserMinus} couleur="#94A3B8" titre="Désinscription"
          sousTitre="Arrêter un cours à la fin du semestre" href="/desinscription"
          testId="espace-cours-desinscription" />
      </Groupe>

      {feuille && personnes.some((p) => p.id === feuille.id) && (
        <FeuillePhoto eleve={personnes.find((p) => p.id === feuille.id)} teinte={feuille.teinte}
          onFermer={() => setFeuille(null)}
          onChange={(choix) => {
            setRetouches((r) => ({ ...r, [feuille.id]: choix }));
            onRecharger?.();
          }} />
      )}
    </div>
  );
};
